import { useMemo } from 'react';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js';
import type { CategoryMapPoint } from './types';

// Canvas-backed scatter (chart.js): one <canvas> + internal hit-testing, instead of
// recharts' one SVG node + React handler per point — scales to thousands of clusters.
// `<Scatter>` from react-chartjs-2 registers its own controller; we register the
// scales/elements/plugins it needs.
ChartJS.register(LinearScale, PointElement, Tooltip);

// rgb tuples mirroring the Tailwind palette in statusColor.ts; kept as rgb here so the
// per-point alpha (dim / emphasis / hover) can be applied on the canvas fill.
const STATUS_RGB: Record<CategoryMapPoint['status'], string> = {
  normal: '212, 212, 216', // zinc-300
  borderline: '245, 158, 11', // amber-500
  misplaced: '239, 68, 68', // red-500
};

function pointColor(
  point: CategoryMapPoint,
  hoveredId: number | null,
  emphasizedCategoryIds: Set<number> | null,
): string {
  const dimmed = emphasizedCategoryIds !== null && !emphasizedCategoryIds.has(point.categoryId);
  const alpha = hoveredId === point.id ? 0.95 : dimmed ? 0.08 : point.status === 'normal' ? 0.35 : 0.85;
  return `rgba(${STATUS_RGB[point.status]}, ${alpha})`;
}

export function CategoryMapScatter({
  points,
  emphasizedCategoryIds,
  hoveredId,
  onHover,
  onSelect,
}: {
  points: CategoryMapPoint[];
  emphasizedCategoryIds: Set<number> | null;
  hoveredId: number | null;
  onHover: (id: number | null) => void;
  onSelect: (clusterId: number) => void;
}) {
  // Rebuilds only when points / emphasis / the hovered point change — not per mouse-move,
  // since onHover sets the same id while the cursor stays on one dot (React bails on equal state).
  const data = useMemo<ChartData<'scatter'>>(() => {
    const maxPageCount = points.reduce((max, point) => Math.max(max, point.pageCount), 1);
    return {
      datasets: [
        {
          data: points.map((point) => ({ x: point.x, y: point.y })),
          pointRadius: points.map((point) => {
            const base = 3 + 9 * Math.sqrt(point.pageCount / maxPageCount);
            return hoveredId === point.id ? base + 2 : base;
          }),
          pointHitRadius: 6,
          pointBackgroundColor: points.map((point) => pointColor(point, hoveredId, emphasizedCategoryIds)),
          pointBorderWidth: 0,
        },
      ],
    };
  }, [points, hoveredId, emphasizedCategoryIds]);

  const options = useMemo<ChartOptions<'scatter'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'nearest', intersect: true },
      onHover: (event, elements) => {
        const target = event.native?.target as HTMLElement | undefined;
        if (target) {
          target.style.cursor = elements.length ? 'pointer' : 'default';
        }
        // Only set on a dot; don't clear over empty gaps (that would flicker the
        // highlight and clobber the worklist→dot sync). Clearing happens on
        // onMouseLeave of the whole canvas, mirroring the old recharts behavior.
        if (elements.length) {
          onHover(points[elements[0].index].id);
        }
      },
      onClick: (_event, elements) => {
        if (elements.length) {
          onSelect(points[elements[0].index].id);
        }
      },
      scales: {
        x: { display: false },
        y: { display: false },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            title: () => '',
            label: (ctx) => {
              const point = points[ctx.dataIndex];
              const lines = [`${point.categoryName} · #${point.id}`, `${point.pageCount} pages`];
              if (point.status !== 'normal' && point.suggestedCategoryName) {
                lines.push(
                  `${point.status === 'misplaced' ? 'Closer to' : 'Near'} ${point.suggestedCategoryName} (margin ${point.margin.toFixed(3)})`,
                );
              }
              return lines;
            },
          },
        },
      },
    }),
    [points, onHover, onSelect],
  );

  return (
    <div className="h-80 w-full" onMouseLeave={() => onHover(null)}>
      <Scatter data={data} options={options} />
    </div>
  );
}
